import { FileMinusIcon, ReceiptTextIcon, SearchIcon } from "lucide-react"
import type * as React from "react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import {
  ActiveCreditNoteFilterChips,
  CreditNoteFilterSheet,
} from "@/features/credit-notes/CreditNoteFilterSheet"
import {
  CREDIT_NOTE_PAGE_SIZE,
  useCreditNoteFilters,
  useCreditNoteList,
  type CreditNoteSortKey,
} from "@/features/credit-notes/use-credit-note-list"
import { DocumentStatusBadge, useStatusLabel } from "@/features/invoices/DocumentStatusBadge"
import { FeatureOff } from "@/components/FeatureOff"
import { SortableHead } from "@/components/SortableHead"
import { SyncStatus } from "@/components/SyncStatus"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ApiError } from "@/lib/api-error"
import { useFormatDay } from "@/lib/dates"
import type { CreditNoteListRow } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * The credit-note register (§17.3, section 34(1) CGST).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT LOOKS LIKE THE OTHER THREE REGISTERS, DELIBERATELY — the owner has asked
 * twice that the four documents differ only where the documents genuinely do.
 * Same toolbar shape (a number search, a status select, everything else behind
 * "More filters"), same phone-card / desktop-table pair, same sortable heads,
 * same pager, same "Showing 1–50 of 214".
 *
 * WHERE IT GENUINELY DIFFERS, AND WHY:
 *
 *   THERE IS NO "NEW CREDIT NOTE" BUTTON. §17.3 rule 1 — a credit note is
 *   RAISED FROM THE BILL IT CORRECTS, so the only way in is the button on the
 *   invoice detail screen. A "+" here would open a form asking which invoice,
 *   which is exactly the way the wrong one gets picked. The empty state says so
 *   in words rather than leaving the absence to be puzzled over.
 *
 *   THERE IS NO DRAFTS STRIP. There are no credit-note drafts: `DraftKind` is a
 *   database enum with no member for one, because a credit note is short and is
 *   typed and issued in one sitting.
 *
 *   EVERY ROW NAMES THE BILL IT REDUCES, and that is the column the other three
 *   registers have no counterpart for. §34's link is not only a detail-screen
 *   field: a list of credit notes that does not say what each one reduces cannot
 *   be read at all.
 *
 * CANCELLED NOTES ARE SHOWN BY DEFAULT — the INVOICE rule, not the
 * quotation/challan one. §34(1) requires a consecutive series and the number
 * stays consumed, so a number that simply vanished from the register would be a
 * question from the department that nobody could answer.
 *
 * NOTHING HERE PRINTS A MINUS SIGN. The amount is stored positive and the
 * document type carries the direction (§17.3); the column heading says
 * "Credited", which is what makes the missing sign safe.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function CreditNoteListScreen(): React.JSX.Element {
  const { t } = useTranslation()
  /*
   * `credit_note_date` is a CALENDAR DAY (`YYYY-MM-DD`) and goes STRAIGHT here.
   * Never `new Date(row.date)` — that parses a calendar day as UTC midnight and
   * renders the 9th for the 10th on any browser west of Greenwich.
   */
  const formatDay = useFormatDay()
  const statusLabel = useStatusLabel()

  const { filters, setFilters, clearFilters, activeCount } = useCreditNoteFilters()
  const query = useCreditNoteList(filters)

  const sortBy = (sort: CreditNoteSortKey | undefined, dir: "asc" | "desc" | undefined) => {
    setFilters({ sort, dir })
  }

  const rows = query.data?.data ?? []
  const total = query.data?.total ?? 0
  const first = total === 0 ? 0 : filters.offset + 1
  const last = Math.min(filters.offset + rows.length, total)

  /*
   * §18.3 — A 404 HERE MEANS THE FEATURE IS OFF, NOT THAT SOMETHING IS MISSING,
   * and today it is the ORDINARY answer: `credit_notes` ships DISABLED.
   *
   * Every route in `apps/api/src/routes/credit-notes.ts` sits behind
   * `requireFlag("credit_notes")`, which runs BEFORE `requireCap` and answers
   * 404 rather than 403 precisely so that "forbidden" cannot leak the existence
   * of unreleased work. The client half of that contract is this branch: it must
   * NOT say "you do not have permission" — which is what a leaked 403 would look
   * like — and must offer no retry, because pressing a button cannot flip a flag.
   */
  if (query.error instanceof ApiError && query.error.status === 404) {
    return <CreditNotesNotSwitchedOn />
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("nav.creditNotes")}</h1>
          <p className="text-muted-foreground max-w-2xl text-sm">
            {t("creditNote.listSubtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* A note can be raised or cancelled on the shop computer while this
            * screen is open, and cancelling one changes what a customer owes. */}
          <SyncStatus
            updatedAt={query.dataUpdatedAt}
            syncing={query.isFetching}
            onSync={() => {
              void query.refetch()
            }}
          />
        </div>
      </header>

      <div className="flex flex-col gap-3">
        <CreditNoteFilterBar
          filters={filters}
          setFilters={setFilters}
          clearFilters={clearFilters}
          activeCount={activeCount}
        />
        <ActiveCreditNoteFilterChips filters={filters} setFilters={setFilters} />
      </div>

      {query.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void query.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : query.isPending ? (
        <ListSkeleton />
      ) : rows.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              {/* The same glyph the sidebar and the recents card use — one
                * picture for one document kind (`DOCUMENT_KIND_ICON`). */}
              <FileMinusIcon />
            </EmptyMedia>
            <EmptyTitle>
              {activeCount > 0 ? t("list.noneMatch") : t("creditNote.emptyTitle")}
            </EmptyTitle>
            <EmptyDescription>
              {activeCount > 0 ? t("list.noneMatchHelp") : t("creditNote.emptyHelp")}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            {activeCount > 0 ? (
              <Button variant="outline" onClick={clearFilters}>
                {t("list.clearFilters")}
              </Button>
            ) : (
              /*
               * THE EMPTY STATE POINTS AT THE BILLS, NOT AT A "NEW" BUTTON —
               * §17.3 rule 1. There is nothing to create from here, and saying
               * where a credit note actually comes from is more use than a
               * control that would ask the wrong question.
               */
              <Button variant="outline" nativeButton={false} render={<Link to={routes.invoices} />}>
                <ReceiptTextIcon />
                {t("creditNote.emptyAction")}
              </Button>
            )}
          </EmptyContent>
        </Empty>
      ) : (
        <>
          {/* Phone: one card per note. Not a cut-down table — the same facts,
            * stacked, because this is the layout the owner actually reads. */}
          <Card className="overflow-hidden py-0 md:hidden">
            <ul className="flex flex-col">
              {rows.map((row) => (
                <li key={row.id} className="border-b last:border-b-0">
                  <Link
                    to={routes.creditNote(row.id)}
                    aria-label={t("list.rowLabel", {
                      number: row.number,
                      customer: row.customerName ?? t("lbl.customerUnknown"),
                      date: formatDay(row.date),
                      status: statusLabel(row.status, "creditNote"),
                    })}
                    className="hover:bg-accent/50 relative flex min-h-14 items-start gap-3 p-4 transition-colors"
                  >
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-medium tabular-nums">{row.number}</span>
                        <DocumentStatusBadge
                          status={row.status}
                          kind="creditNote"
                          className="text-xs"
                        />
                      </span>
                      <span className="truncate text-sm">
                        {row.customerName === null ? (
                          <span className="text-muted-foreground">
                            {t("lbl.customerUnknown")}
                          </span>
                        ) : (
                          row.customerName
                        )}
                      </span>
                      <AgainstBill row={row} />
                      <span className="text-muted-foreground text-xs tabular-nums">
                        {formatDay(row.date)}
                      </span>
                      <span className="text-muted-foreground line-clamp-2 text-xs">
                        {row.reason}
                      </span>
                    </span>
                    <CreditedAmount row={row} />
                  </Link>
                </li>
              ))}
            </ul>
          </Card>

          {/* Desktop. */}
          <Card className="hidden overflow-hidden py-0 md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead column="number" label={t("lbl.number")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="date" label={t("lbl.date")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="customer" label={t("lbl.customer")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  {/* NOT SORTABLE — `CreditNoteListQuerySchema` is `.strict()`
                    * and has no `invoice` sort key, so a head that offered one
                    * would earn a 400. §34's link is a column, not an order.
                    * A plain `TableHead`, never a `TableCell`: a `<td>` inside
                    * `<thead>` is invalid markup and drops the column header
                    * out of the table's accessible structure. */}
                  <TableHead>{t("creditNote.againstColumn")}</TableHead>
                  <SortableHead column="status" label={t("lbl.status")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="total" align="end" label={t("creditNote.creditedColumn")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    /* `relative` IS LOAD-BEARING. The number's stretched link
                     * (`after:absolute after:inset-0`) resolves against the
                     * nearest POSITIONED ancestor — without this that is the
                     * page, and a click anywhere opens the last row. */
                    className="hover:bg-accent/50 relative cursor-pointer"
                  >
                    <TableCell className="font-medium tabular-nums">
                      <Link
                        to={routes.creditNote(row.id)}
                        className="after:absolute after:inset-0 hover:underline"
                      >
                        {row.number}
                      </Link>
                    </TableCell>
                    <TableCell className="tabular-nums whitespace-nowrap">
                      {formatDay(row.date)}
                    </TableCell>
                    <TableCell className="max-w-[22ch] truncate">
                      {row.customerName === null ? (
                        <span className="text-muted-foreground">{t("lbl.customerUnknown")}</span>
                      ) : (
                        row.customerName
                      )}
                    </TableCell>
                    <TableCell className="max-w-[26ch]">
                      <AgainstBill row={row} />
                      <span className="text-muted-foreground line-clamp-1 text-xs">
                        {row.reason}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DocumentStatusBadge status={row.status} kind="creditNote" />
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <CreditedAmount row={row} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Pager
            first={first}
            last={last}
            total={total}
            offset={filters.offset}
            onOffset={(offset) => {
              setFilters({ offset })
              window.scrollTo({ top: 0, behavior: "smooth" })
            }}
          />
        </>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Row parts                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The bill this note reduces — §34's link, on every row.
 *
 * A REAL HYPERLINK, because `invoiceId` is a genuine foreign key the API refuses
 * to write without: unlike an invoice's free-typed `challanNumber`, there is no
 * chance of this text naming somebody else's document. The NUMBER shown is the
 * frozen copy that prints on the sheet; the link uses the id, so a note still
 * opens the right bill.
 *
 * `relative z-10` PUTS IT ABOVE THE ROW'S STRETCHED LINK — without it the
 * invoice link would be swallowed and open the credit note instead of the bill
 * it names, which is the mistake the challan register's `BilledMark` records.
 */
function AgainstBill({ row }: { row: CreditNoteListRow }): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <Link
      to={routes.invoice(row.invoiceId)}
      className="hover:text-foreground text-muted-foreground relative z-10 inline-flex items-center gap-1 text-sm hover:underline"
    >
      <ReceiptTextIcon aria-hidden="true" className="size-3.5" />
      <span className="tabular-nums">
        {t("creditNote.againstRow", { number: row.originalInvoiceNumber })}
      </span>
    </Link>
  )
}

/**
 * The amount credited back — REAL MONEY, POSITIVE, AND NEVER WITH A MINUS SIGN.
 *
 * Unlike a challan's declared value this figure is money: it is output tax and
 * taxable value being reversed, and the customer owes exactly this much less.
 * Unlike a bill's total it SUBTRACTS — which the column heading says
 * ("Credited"), because the sign that would otherwise say it is deliberately not
 * there (§17.3: a minus in the data is how a sum silently changes direction).
 *
 * `formatRupees` is what puts the Indian digit grouping on it — `₹1,45,600.00`,
 * never `₹145,600.00`, and never `toLocaleString`.
 */
function CreditedAmount({ row }: { row: CreditNoteListRow }): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <span className="flex shrink-0 flex-col items-end">
      <span className="font-medium tabular-nums">{formatRupees(row.grandTotalPaise)}</span>
      {/*
        * A CANCELLED NOTE CREDITS NOTHING, said beside the figure rather than
        * left to the badge alone. The row still shows the amount — the document
        * exists and says that number — but every sum in the system counts ISSUED
        * rows only, so this one is not coming off any bill.
        */}
      {row.status === "CANCELLED" ? (
        <span className="text-muted-foreground text-xs">{t("creditNote.creditsNothing")}</span>
      ) : null}
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* The flag is off (§18.3)                                                     */
/* -------------------------------------------------------------------------- */

/** What the register looks like when `requireFlag("credit_notes")` refuses. */
function CreditNotesNotSwitchedOn(): React.JSX.Element {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t("nav.creditNotes")}</h1>
      </header>
      {/* ONE COMPONENT AND ONE PAIR OF STRINGS FOR EVERY FLAGGED MODULE —
        * `components/FeatureOff.tsx`, named from the nav label so the panel and
        * the sidebar row it sits under say the same word. */}
      <FeatureOff module={t("nav.creditNotes")} icon={<FileMinusIcon />} />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* The toolbar                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The filters the API actually supports — and no others.
 *
 * THE SAME SHAPE AS THE OTHER THREE REGISTERS' TOOLBARS (§11.1): a number
 * search, the status, and everything else behind one "More filters" button. The
 * four registers sit next to each other in the sidebar and the owner moves
 * between them all day; a filter that lives in a different place on each is a
 * filter he has to hunt for four times.
 */
function CreditNoteFilterBar({
  filters,
  setFilters,
  clearFilters,
  activeCount,
}: {
  filters: ReturnType<typeof useCreditNoteFilters>["filters"]
  setFilters: ReturnType<typeof useCreditNoteFilters>["setFilters"]
  clearFilters: () => void
  activeCount: number
}): React.JSX.Element {
  const { t } = useTranslation()

  /*
   * The two CreditNoteStatus values plus "no filter". Declared once and handed
   * BOTH to `<Select items={…}>` and to the items themselves: Base UI resolves
   * the trigger's text from that array, and without it the trigger would print
   * the raw `CANCELLED` — in English, on a Marathi phone.
   */
  const statusOptions = [
    { value: "", label: t("list.anyStatus") },
    { value: "ISSUED", label: t("status.issued") },
    { value: "CANCELLED", label: t("status.cancelled") },
  ]

  return (
    <div className="flex flex-wrap items-end gap-3">
      <CreditNoteSearchBox filters={filters} setFilters={setFilters} />

      <div className="flex min-w-44 flex-col gap-1.5">
        <Label htmlFor="credit-note-status">{t("list.statusFilter")}</Label>
        <Select
          items={statusOptions}
          value={filters.status ?? ""}
          onValueChange={(value) => {
            /*
             * "" MEANS NO FILTER. The list schema is `.strict()`, so `?status=`
             * is a 400 rather than a no-op. Base UI hands the value over
             * directly — there is no `event.target` — and types it nullable, so
             * null folds into the same branch.
             */
            setFilters({ status: value === "" || value == null ? undefined : String(value) })
          }}
        >
          {/* The id goes on the TRIGGER, which is the button `<Label htmlFor>`
            * labels — a <button> is a labelable element, so
            * `getByLabelText("Status")` still resolves this control. */}
          <SelectTrigger id="credit-note-status" className="w-full">
            <SelectValue placeholder={t("list.anyStatus")} />
          </SelectTrigger>
          <SelectContent className="w-auto min-w-(--anchor-width) max-w-[min(90vw,28rem)]">
            {statusOptions.map((option) => (
              /* `data-value` carries the MACHINE value onto the option element.
               * Base UI puts no value attribute on it, and a test checking these
               * are the CREDIT NOTE states rather than the invoice ones would
               * otherwise be reduced to reading translated labels. */
              <SelectItem
                key={option.value}
                value={option.value}
                data-value={option.value}
                className="pl-3"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <CreditNoteFilterSheet
        filters={filters}
        setFilters={setFilters}
        clearFilters={clearFilters}
        activeCount={activeCount}
      />
    </div>
  )
}

/**
 * The number search.
 *
 * IT MATCHES THE NOTE'S OWN NUMBER **AND** THE FROZEN ORIGINAL INVOICE NUMBER,
 * which is the one thing worth knowing about this box: typing a BILL number here
 * finds every credit note raised against it. It is a SERVER search (`?q=`), so it
 * covers the whole book rather than the fifty rows on this page, and it does NOT
 * match the customer name — that lives in a text-backed JSON column the server
 * will not query inside (CLAUDE.md).
 *
 * DEBOUNCED, and mirrored in local state rather than read straight from the URL.
 * Writing every keystroke to the URL fires a request per character and re-renders
 * the whole screen through the router, which on a phone feels like dropped
 * letters.
 */
function CreditNoteSearchBox({
  filters,
  setFilters,
}: {
  filters: ReturnType<typeof useCreditNoteFilters>["filters"]
  setFilters: ReturnType<typeof useCreditNoteFilters>["setFilters"]
}): React.JSX.Element {
  const { t } = useTranslation()
  const [text, setText] = useState(filters.q ?? "")

  // Follow the URL when it changes elsewhere — back button, "clear filters".
  useEffect(() => {
    setText(filters.q ?? "")
  }, [filters.q])

  useEffect(() => {
    const current = filters.q ?? ""
    if (text === current) return
    const timer = window.setTimeout(() => {
      setFilters({ q: text.trim() === "" ? undefined : text.trim() })
    }, 300)
    return () => {
      window.clearTimeout(timer)
    }
  }, [text, filters.q, setFilters])

  return (
    <div className="flex min-w-56 flex-1 flex-col gap-1.5 sm:max-w-xs">
      <Label htmlFor="credit-note-search">{t("list.searchCreditNotes")}</Label>
      <div className="relative">
        <SearchIcon
          aria-hidden="true"
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
        />
        <Input
          id="credit-note-search"
          type="search"
          className="pl-9 tabular-nums"
          placeholder={t("list.searchCreditNotesPlaceholder")}
          value={text}
          onChange={(event) => {
            setText(event.target.value)
          }}
        />
      </div>
    </div>
  )
}

function Pager({
  first,
  last,
  total,
  offset,
  onOffset,
}: {
  first: number
  last: number
  total: number
  offset: number
  onOffset: (offset: number) => void
}): React.JSX.Element | null {
  const { t } = useTranslation()
  if (total <= CREDIT_NOTE_PAGE_SIZE) return null

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-muted-foreground text-sm tabular-nums">
        {t("list.showingRange", { first, last, total })}
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          disabled={offset === 0}
          onClick={() => {
            onOffset(Math.max(0, offset - CREDIT_NOTE_PAGE_SIZE))
          }}
        >
          {t("btn.previous")}
        </Button>
        <Button
          variant="outline"
          disabled={last >= total}
          onClick={() => {
            onOffset(offset + CREDIT_NOTE_PAGE_SIZE)
          }}
        >
          {t("btn.next")}
        </Button>
      </div>
    </div>
  )
}

function ListSkeleton(): React.JSX.Element {
  return (
    <Card className="overflow-hidden py-0">
      <ul className="flex flex-col">
        {[0, 1, 2, 3, 4].map((index) => (
          <li key={index} className="flex items-center gap-3 border-b p-4 last:border-b-0">
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-44" />
            </div>
            <Skeleton className="h-4 w-24" />
          </li>
        ))}
      </ul>
    </Card>
  )
}
